import React, { Fragment, useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { bool, func, shape } from "prop-types";
import { routerActions } from "connected-react-router";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as settingsActions } from "reducers/settings";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import CustomData from "pages/_components/CustomData/CustomData";
import { Col, Row } from "react-bootstrap";

import { Field, Form, Formik } from "formik";
import Selector from "pages/_components/fields/formik/Selector";
import { ResponsiveHeader } from "pages/_components/header";
import MultiSelectButton from "pages/_components/fields/formik/MultiSelectButton";
import TextField from "pages/_components/fields/TextField";
import gaEventTracker, { events } from "util/gaEventTracker";
import GridTable from "pages/_components/GridTable/GridTable";
import { Checkbox } from "pages/_components/fields/Checkbox";
import I18n from "pages/_components/I18n";

const CATEGORY = "newAccount";

const NewAccountRequestForm = ({ dispatch, fetching, newAccountData, showlastValues, isDesktop }) => {
    const isLoading = fetching || !newAccountData;
    const [acceptTerm, setAcceptTerm] = useState(false);
    const { termsAndConditions } = useSelector((state) => state.settings);

    const newAccountFormData = {
        // productTypes: configUtils.getArray("account.accountTypes", ["CC-USD", "CE-USD", "CC-ARS", "CE-ARS"]),
        productTypes: configUtils.getArray("account.accountTypes", ["CC-ARS", "CE-ARS"]),
        periodicity: configUtils.getArray("account.periodicities", ["WEEKLY", "MONTHLY", "DAILY"]),
        purpose: configUtils.getArray("account.purposes", ["INCOME", "INVESTMENT", "LOB", "OTHER"]),
        modalities: configUtils.getArray("account.modalities", ["CHECKS", "TRANSFER", "DEPOSIT"]),
        conditionsVersion: configUtils.get("account.newAccount.conditions.version", 1),
    };

    useEffect(
        () => () => {
            dispatch(accountsActions.newAccountDataShouldShowLastValues(false));
        },
        [],
    );

    useEffect(() => {
        dispatch(settingsActions.getTermsAndConditions(CATEGORY));
    }, []);

    // Formik Validation Control
    const FORM_ID = "NewAccountRequestForm";
    const REQUIRED_TEXT = i18n.get("deposits.request.required");

    const submitForm = async (event, allFormikProps) => {
        if (event) {
            event.preventDefault();
        }

        let allFieldsValids = true;
        const fields = Object.keys(allFormikProps.values);
        for (let i = 0; i < fields.length; i++) {
            // eslint-disable-next-line no-await-in-loop, no-use-before-define
            const handleBlurRes = await handleBlur(fields[i], allFormikProps);
            if (handleBlurRes) {
                allFieldsValids = false;
            }
        }
        if (allFieldsValids) {
            allFormikProps.submitForm();
        }
    };

    const handleBlur = async (field, allFormikProps) => {
        allFormikProps.setFieldTouched(field, true);
        // eslint-disable-next-line no-use-before-define
        const fieldValidationRes = await validateField(field, allFormikProps.values);
        allFormikProps.setFieldError(field, fieldValidationRes);
        if (allFormikProps.values.modalities.length > 0 || allFormikProps.values.modalityReason !== "") {
            allFormikProps.setFieldError("modalities", null);
        }
        if (allFormikProps.values.purpose === "OTHER" && allFormikProps.values.purposeReason !== "") {
            allFormikProps.setFieldError("purposeReason", null);
        }
        return fieldValidationRes;
    };
    const validateField = async (field, values) => {
        const fieldValidationSchemas = {
            productType: Yup.string()
                .oneOf(newAccountFormData.productTypes)
                .required(REQUIRED_TEXT),
            periodicity: Yup.string()
                .oneOf(newAccountFormData.periodicity)
                .required(REQUIRED_TEXT),
            purpose: Yup.string()
                .oneOf(newAccountFormData.purpose)
                .required(REQUIRED_TEXT),
            // eslint-disable-next-line no-confusing-arrow
            purposeReason: Yup.string().when("$purpose", (purpose, schema) =>
                purpose === "OTHER" ? schema.required(REQUIRED_TEXT) : schema,
            ),
            modalities: Yup.array()
                .of(Yup.string())
                // eslint-disable-next-line no-confusing-arrow
                .when("$modalityReason", (modalityReason, schema) =>
                    !modalityReason ? schema.min(1, REQUIRED_TEXT) : schema,
                ),
            modalityReason: Yup.string(),
            messageToBank: Yup.string(),
            termAndConditions: Yup.boolean(),
        };

        // eslint-disable-next-line no-return-await
        return await fieldValidationSchemas[field]
            .validate(values[field], { context: values })
            .then(() => null)
            .catch((err) => err.message);
    };

    const formikProps = {
        initialValues: {
            productType: showlastValues ? newAccountData?.account?.productType || "" : "",
            periodicity: showlastValues ? newAccountData?.periodicity || "" : "",
            purpose: showlastValues ? newAccountData?.purpose || "" : "",
            purposeReason: showlastValues ? newAccountData?.purposeReason || "" : "",
            modalities: showlastValues ? newAccountData?.modalities || [] : [],
            modalityReason: showlastValues ? newAccountData?.modalityReason || "" : "",
            messageToBank: showlastValues ? newAccountData?.messageToBank || "" : "",
            termAndConditions: showlastValues ? newAccountData?.termAndConditions || false : false,
        },
        onSubmit: ({ productType, ...values }) => {
            const { modalityReason, modalities } = values;
            const _modalities = modalityReason ? [...modalities, "OTHER"] : modalities;
            // gaEventTracker.triggerEvent(events.accountRequest1, {
            //     productType,
            //     periodicity: values?.periodicity,
            // });
            gaEventTracker.triggerEvent(
                events.cuentas_solicitud({
                    numCuenta: null,
                    tipoCuenta: productType,
                    periodicidad: values?.periodicity,
                    destino: values?.purpose,
                    otro: values?.purposeReason,
                    modalidades: modalityReason ? [...modalities, modalityReason] : modalities,
                    mensaje: values?.messageToBank,
                    termAndConditions: values?.termAndConditions,
                }),
            );

            dispatch(
                accountsActions.setNewAccountData({
                    account: { productType },
                    ...values,
                    conditionsVersion: newAccountFormData.conditionsVersion,
                    modalities: _modalities,
                }),
            );
            dispatch(accountsActions.setNewAccountTicketSignable(false));
            dispatch(routerActions.push("/accounts/summaryForm"));
        },
        validateOnChange: false,
        validateOnBlur: false,
        enableReinitialize: true,
    };

    const hadleIsValid = (allFormikProps) => {
        let valOne = true;
        let valTwo = true;

        if (allFormikProps.values.productType && allFormikProps.values.periodicity && allFormikProps.values.purpose) {
            valOne = false;
        }
        if (allFormikProps.values.modalities.length > 0 || allFormikProps.values.modalityReason) {
            valTwo = false;
        }
        return valOne || valTwo || !allFormikProps.values.termAndConditions;
    };

    return (
        <Fragment>
            <Notification scopeToShow="accounts" />
            <ResponsiveHeader id="accounts.create.title" />
            <MainContainer showLoader={isLoading}>
                <Formik {...formikProps}>
                    {(allFormikProps) => (
                        <Form id={FORM_ID} onSubmit={(e) => submitForm(e, allFormikProps)}>
                            <Row className="form-background">
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="productType"
                                        onBlur={() => handleBlur("productType", allFormikProps)}
                                        label={`${i18n.get("accounts.create.accountType")}`}
                                        placeholder={i18n.get("global.select.type.account.placeholder")}
                                        component={Selector}
                                        options={newAccountFormData.productTypes.map((value) => ({
                                            value,
                                            label: i18n.get(`accounts.create.accountType.${value}`),
                                        }))}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        className="grid-table-data"
                                        idForm={FORM_ID}
                                        name="periodicity"
                                        onBlur={() => handleBlur("periodicity", allFormikProps)}
                                        label={`${i18n.get("accounts.create.periodicity")}`}
                                        placeholder={i18n.get("global.select.option.placeholder")}
                                        component={Selector}
                                        options={newAccountFormData.periodicity.map((value) => ({
                                            value,
                                            label: i18n.get(`accounts.create.periodicity.${value}`),
                                        }))}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="purpose"
                                        onBlur={() => handleBlur("purpose", allFormikProps)}
                                        label={`${i18n.get("accounts.create.purpose")}`}
                                        placeholder={i18n.get("accounts.purpose.placeholder")}
                                        component={Selector}
                                        options={newAccountFormData.purpose.map((value) => ({
                                            value,
                                            label: i18n.get(`accounts.create.purpose.${value}`),
                                        }))}
                                    />
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field idForm={FORM_ID} name="purposeReason">
                                        {({ field, form }) => (
                                            <TextField
                                                field={field}
                                                form={form}
                                                idForm={FORM_ID}
                                                onBlur={() => handleBlur("purposeReason", allFormikProps)}
                                                type={form.values.purpose !== "OTHER" ? "hidden" : "text"}
                                                disabled={form.values.purpose !== "OTHER"}
                                                hidePlaceholder
                                                labelText="accounts.create.other"
                                            />
                                        )}
                                    </Field>
                                </Col>
                                <Col xs={12} md={10} mdOffset={1}>
                                    <div className="modality-wrapper">
                                        <Field
                                            className="grid-table-data"
                                            idForm={FORM_ID}
                                            name="modalities"
                                            onBlur={() => handleBlur("modalities", allFormikProps)}
                                            label={`${i18n.get("accounts.create.modalities")}`}
                                            component={MultiSelectButton}
                                            options={newAccountFormData.modalities.map((value) => ({
                                                value,
                                                label: `accounts.create.modality.${value}`,
                                            }))}
                                        />
                                        <Field
                                            idForm={FORM_ID}
                                            name="modalityReason"
                                            onBlur={() => handleBlur("modalityReason", allFormikProps)}
                                            component={TextField}
                                            hidePlaceholder
                                            type="text"
                                            labelText="accounts.create.other"
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} md={10} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="messageToBank"
                                        onBlur={() => handleBlur("messageToBank", allFormikProps)}
                                        component={TextField}
                                        hidePlaceholder
                                        type="text"
                                        renderAs="textarea"
                                        maxLength={500}
                                        labelText="accounts.create.messageToBank"
                                    />
                                </Col>
                                <Col xs={12} md={10} mdOffset={1}>
                                    <div className="box info flex">
                                        <CustomData
                                            dataClass={!isDesktop && "mb-7"}
                                            keepSpaceIfNoValue={false}
                                            icon="images/information_fill.svg"
                                            titleText={i18n.get("account.new.text")}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Col xs={12} md={10} mdOffset={2} className="mt-10">
                                <div className="box info mt-6 mb-8">
                                    <Checkbox
                                        name="acceptBtn"
                                        label={termsAndConditions && <I18n defaultValue={termsAndConditions} />}
                                        onChange={() => setAcceptTerm(!acceptTerm)}
                                        value={acceptTerm}
                                        checked={acceptTerm}
                                        formGroup={false}
                                    />
                                </div>
                            </Col>
                            <GridTable className="form-actions grid-table-buttons mb-5">
                                <GridTable.Container columns={12} columnsTemplate="fixed">
                                    <GridTable.Data columnStart={7} columnWidth={4}>
                                        <CustomButton
                                            text={i18n.get("global.continue")}
                                            type="submit"
                                            className="btn-primary flex"
                                            disabled={hadleIsValid(allFormikProps) && !acceptTerm}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={3} columnWidth={4}>
                                        <CustomButton
                                            text={i18n.get("global.cancel")}
                                            func={() => dispatch(routerActions.push("/accounts"))}
                                            className="btn-outline flex"
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </Fragment>
    );
};

NewAccountRequestForm.propTypes = {
    newAccountData: shape({}).isRequired,
    showlastValues: bool.isRequired,
    dispatch: func.isRequired,
    fetching: bool,
    isDesktop: bool,
};

NewAccountRequestForm.defaultProps = {
    fetching: false,
    isDesktop: false,
};

const mapStateToProps = (state) => ({
    newAccountData: accountsSelectors.getNewAccountData(state),
    showlastValues: accountsSelectors.getNewAccountDataShouldShowLastValues(state),
    fetching: accountsSelectors.getFetching(state),
});

export default connect(mapStateToProps)(NewAccountRequestForm);
